import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, ASSETS } from "../lib/site";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center group">
    <img
      src={ASSETS.brandLogo}
      alt="Ascendance International"
      className="h-[50px] md:h-[56px] w-auto object-contain transition-opacity duration-300 group-hover:opacity-80"
    />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="global-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "backdrop-blur-xl bg-surface/70 border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`relative font-sans text-sm uppercase tracking-[0.15em] transition-colors duration-300 ${
                    active ? "text-textmain" : "text-textmuted hover:text-textmain"
                  }`}
                >
                  {link.label}
                  {active && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute -bottom-2 left-0 right-0 h-px bg-steel"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden lg:inline-flex items-center gap-2 text-sm font-sans font-semibold uppercase tracking-[0.15em] border border-white/20 hover:border-steel hover:text-steel px-5 py-2.5 transition-colors duration-300 whitespace-nowrap shrink-0"
          >
            Get In Touch <ArrowUpRight size={16} />
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-textmain"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink/98 backdrop-blur-lg lg:hidden flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                >
                  <Link
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    className="font-heading text-4xl uppercase tracking-tight text-textmain hover:text-steel transition-colors duration-300 py-2 block"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
